import React, { ChangeEvent, useState } from 'react';
import { Dropdown, Input } from 'react-daisyui';
import { twMerge } from 'tailwind-merge';

export type SuggestionProps = {
  key: string;
  value: string;
};

export type AutocompleteProps = {
  disabled?: boolean;
  error?: string;
  name?: string;
  onChange?: (value: SuggestionProps) => void;
  onSelect?: (value: SuggestionProps) => void;
  placeholder?: string;
  suggestions: SuggestionProps[];
  value?: string;
};

const Autocomplete = ({
  disabled,
  error,
  onChange,
  onSelect,
  placeholder,
  suggestions,
  value,
}: AutocompleteProps) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    SuggestionProps[]
  >([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(false);

  const openDropdown = (nextSuggestions: SuggestionProps[]) => {
    setActiveSuggestionIndex(0);
    setFilteredSuggestions(nextSuggestions);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setActiveSuggestionIndex(0);
    setFilteredSuggestions([]);
    setIsOpen(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    const filtered = suggestions.filter(
      (suggestion) =>
        suggestion.value.toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
    );

    openDropdown(filtered);
  };

  const handleFocus = () => {
    openDropdown(suggestions);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }

    closeDropdown();
  };

  const handleSelect = (suggestion: SuggestionProps) => {
    if (onChange) {
      onChange(suggestion);
    }

    if (onSelect) {
      onSelect(suggestion);
    }

    closeDropdown();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const suggestion = filteredSuggestions[activeSuggestionIndex];

      if (!suggestion) {
        return;
      }

      handleSelect(suggestion);
    } else if (e.key === 'ArrowUp') {
      if (activeSuggestionIndex === 0) {
        return;
      }
      setActiveSuggestionIndex(activeSuggestionIndex - 1);
    } else if (e.key === 'ArrowDown') {
      if (activeSuggestionIndex === filteredSuggestions.length - 1) {
        return;
      }
      setActiveSuggestionIndex(activeSuggestionIndex + 1);
    }
  };

  return (
    <div className="relative w-full" onBlur={handleBlur}>
      <Dropdown className={twMerge('w-full', isOpen && 'dropdown-open')}>
        <Dropdown.Toggle button={false}>
          <Input
            className={twMerge(
              'w-full text-balance text-start',
              error ? 'input-error' : '',
              'focus:!bg-sr-white',
              '!border-solid',
            )}
            data-testid="custom-element"
            disabled={disabled}
            type="button"
            value={value}
            onFocus={handleFocus}
          />
          {error && (
            <div aria-live="polite" className="label absolute">
              <span className="label-text-alt text-error">{error}</span>
            </div>
          )}
        </Dropdown.Toggle>
        {isOpen && (
          <Dropdown.Menu className="workspaces absolute left-0 top-10 z-50 inline-block max-h-64 w-full overflow-y-auto rounded-lg border border-sr-white text-sr-black">
            <Input
              autoFocus
              className="mb-2.5 w-full"
              disabled={disabled}
              placeholder={placeholder}
              type="text"
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
            {filteredSuggestions.length > 0 ? (
              filteredSuggestions.map((suggestion, index) => (
                <Dropdown.Item
                  key={suggestion.key}
                  className={twMerge(
                    'cursor-pointer px-4 py-2 !text-sr-black',
                    index === activeSuggestionIndex && 'active',
                  )}
                  onMouseDown={() => handleSelect(suggestion)}
                >
                  {suggestion.value}
                </Dropdown.Item>
              ))
            ) : (
              <Dropdown.Item className="flex items-center self-stretch px-3 py-[6px]">
                <div className="flex items-center gap-3 self-stretch">
                  <div className="break-words text-sm text-sr-black">
                    No suggestions available
                  </div>
                </div>
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        )}
      </Dropdown>
    </div>
  );
};

export default Autocomplete;
